<template>
  <PlaceholderComponent v-if="shouldDisplayPlaceholderComponent(isEditMode, mode, visible)" :name="tracking_id" />
  <div
    v-else-if="isEditMode && !isViewOnlyMode && componentPermissions.hasSomethingEditable"
    :style="isEditMode ? omit(style, 'paddingBottom', 'paddingLeft', 'paddingTop', 'paddingRight') : undefined"
  >
    <EditableComponent
      :id="id"
      :active="componentEditMode && componentEditMode?.id === id"
      :duplicate="componentPermissions.canDuplicate"
      :edit="componentPermissions.canEdit"
      :remove="componentPermissions.canRemove"
      :selected="componentSelectedMode && componentSelectedMode.id === id"
      :settings="!usedInSectionId"
      stylable
      :variants="
        componentPermissions.canEdit
          ? {
              title: t('canvasUI.canvasBuilder.link.selectLayout'),
              options: previewTypeOptions,
              selected: data?.preview_type ?? LinkPreviewTypes.SOLID_BUTTON,
            }
          : undefined
      "
      @duplicate="duplicate(id)"
      @edit="setComponentEditMode(ComponentTypes.Link, id)"
      @remove="removeComponentById(id)"
      @select="setComponentSelectedMode(id)"
      @settings="setComponentSettingsMode(id)"
      @style="$emit('style', $event)"
      @variant-change="onVariantChange"
    >
      <div class="cb-link">
        <RawLink
          v-if="hasData"
          :id="id"
          :data="data"
          v-bind="$attrs"
          :style="pick(style, 'paddingBottom', 'paddingLeft', 'paddingTop', 'paddingRight')"
        />
        <EditLink v-else v-bind="$attrs" />
      </div>
    </EditableComponent>
  </div>
  <template v-else>
    <RawLink
      v-if="data?.url || data?.canvas_anchor"
      :id="id"
      :data="data"
      v-bind="$attrs"
      :style="presentationModeStyle"
    />
  </template>
</template>
<script setup lang="ts">
import { omit, pick } from 'lodash-es'
import { type DropdownOption } from 'naive-ui'
import { computed, onMounted, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'

import { adjustThemeByPreviewType, getPreviewTypeOptions } from './Link.util'
import EditLink from './Link.edit.vue'
import RawLink from './Link.raw.vue'
import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'
import { type LinkProps, type ComponentNode, ComponentTypes, LinkPreviewTypes } from '@canvas-builder/types/canvas'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import { shouldDisplayPlaceholderComponent } from '@canvas-builder/util/canvas.util'
import PlaceholderComponent from '@canvas-builder/components/container/PlaceholderComponent/PlaceholderComponent.vue'
import type { CanvasRetrieve } from '@openapi'

const props = withDefaults(
  defineProps<{
    id: ComponentNode['id']
    data?: LinkProps
    when_used_in_section?: ComponentNode['when_used_in_section']
    usedInSectionId?: CanvasRetrieve['id']
    visible?: ComponentNode['visible']
    tracking_id?: string
    style?: ComponentNode['style']
  }>(),
  {
    when_used_in_section: () => defaultWhenUsedInSection,
    usedInSectionId: '',
  },
)

const { id, usedInSectionId, when_used_in_section } = toRefs(props)
const componentPermissions = useComponentPermissions({
  id,
  usedInSectionId,
  when_used_in_section,
})

const { t } = useI18n()
const {
  componentEditMode,
  mode,
  isEditMode,
  justAddedComponentId,
  removeComponentById,
  setComponentSelectedMode,
  componentSelectedMode,
  duplicate,
  isViewOnlyMode,
  setComponentEditMode,
  setComponentSettingsMode,
  updateNodeDataById,
} = useCanvas()

const hasData = computed(() => props.data?.url || props.data?.canvas_anchor || props.data?.file)

const presentationModeStyle = computed(() => {
  return !isEditMode.value || props.usedInSectionId ? props.style : undefined
})

const previewTypeOptions = getPreviewTypeOptions(t)?.map((item) => ({
  icon: item.icon,
  key: item.value,
  label: item.label,
}))

function onVariantChange(selection?: DropdownOption['key']) {
  const variant = selection as LinkPreviewTypes
  const { backgroundColor, color } = adjustThemeByPreviewType(variant, props.data)
  updateNodeDataById(props.id, { ...props.data, background_color: backgroundColor, color, preview_type: variant })
}

onMounted(() => {
  if (justAddedComponentId.value === props.id) {
    justAddedComponentId.value = null
    setComponentEditMode(ComponentTypes.Link, props.id)
  }
})
</script>
<style lang="scss" scoped>
.editable-component {
  :deep(.n-input__textarea-el) {
    font-family: monospace;
    font-size: 14px;
  }
}

.cb-html__textarea :deep(textarea) {
  min-height: 300px;
}
</style>
